import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fontSizes,
  fonts,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import { PlaySubpageHeader } from "./subpage-header";
import { PlayRequestRow, type PlayRequestItem } from "./request-row";

export function PlayRequestsView({
  leagueName,
  playHref,
  stats,
  creditsAvailable,
  groups,
  countLabel,
}: {
  readonly leagueName: string;
  readonly playHref: string;
  readonly stats: readonly { readonly label: string; readonly value: string }[];
  readonly creditsAvailable: boolean;
  readonly groups: readonly {
    readonly heading: string;
    readonly rows: readonly PlayRequestItem[];
  }[];
  readonly countLabel: string;
}) {
  return (
    <main {...stylex.props(styles.main)}>
      <PlaySubpageHeader
        leagueName={leagueName}
        playHref={playHref}
        title="Experience requests"
      />
      <div {...stylex.props(styles.stats)}>
        {!creditsAvailable ? (
          <p {...stylex.props(styles.empty)}>Credits unavailable right now.</p>
        ) : null}
        {stats.map((stat) => (
          <span key={stat.label} {...stylex.props(styles.stat)}>
            <span {...stylex.props(styles.statLabel)}>{stat.label}</span>
            <span {...stylex.props(styles.statValue)}>{stat.value}</span>
          </span>
        ))}
      </div>
      <div {...stylex.props(styles.body)}>
        {groups.length === 0 ? (
          <p {...stylex.props(styles.empty)}>No experience requests yet.</p>
        ) : (
          <div {...stylex.props(styles.groups)}>
            {groups.map((group) => (
              <section key={group.heading}>
                <h2 {...stylex.props(styles.groupHeading)}>{group.heading}</h2>
                {group.rows.map((request) => (
                  <PlayRequestRow key={request.id} request={request} />
                ))}
              </section>
            ))}
            <p {...stylex.props(styles.count)}>{countLabel} shown</p>
          </div>
        )}
      </div>
    </main>
  );
}

const styles = stylex.create({
  main: {
    boxSizing: "border-box",
    width: "100%",
    minWidth: 0,
    flex: 1,
    marginInline: "auto",
    maxWidth: 1200,
    paddingInline: spacing.space32,
    paddingTop: spacing.space32,
    paddingBottom: spacing.space48,
    minHeight: "100svh",
    fontFamily: fonts.sans,
    color: colors.foreground,
  },
  stats: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    columnGap: 28,
    rowGap: spacing.space8,
    paddingTop: spacing.space16,
  },
  stat: {
    display: "inline-flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    columnGap: 6,
  },
  statLabel: {
    fontSize: fontSizes.caption,
    fontWeight: 500,
    textTransform: "uppercase",
    lineHeight: "16px",
    letterSpacing: "0.2px",
    color: colors.muted,
  },
  statValue: {
    fontSize: fontSizes.sectionTitle,
    fontWeight: 600,
    lineHeight: "22px",
  },
  body: { paddingTop: spacing.space24 },
  groups: { display: "flex", flexDirection: "column", gap: 10 },
  groupHeading: {
    margin: 0,
    paddingBottom: spacing.space4,
    fontSize: fontSizes.caption,
    fontWeight: 500,
    lineHeight: "16px",
    color: colors.muted,
  },
  empty: {
    margin: 0,
    fontSize: fontSizes.small,
    fontStyle: "italic",
    lineHeight: "18px",
    color: colors.muted,
  },
  count: {
    margin: 0,
    paddingTop: 6,
    fontSize: fontSizes.caption,
    lineHeight: "16px",
    color: colors.subtle,
  },
});
